import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Search, ChevronRight, CheckCircle2, XCircle, Send, X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useHGS } from '../context/HGSContext';
import { ROLES } from '../data/mockData';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const TYPE_META = {
  handoff: { icon: Send, color: 'bg-amber-100 text-amber-700 border-amber-200', accent: 'text-amber-700' },
  approved: { icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700 border-emerald-200', accent: 'text-emerald-700' },
  rejected: { icon: XCircle, color: 'bg-red-100 text-red-700 border-red-200', accent: 'text-red-700' },
};

export default function TopBar({ title, subtitle, breadcrumbs = [], actions }) {
  const { user, notifications, markNotificationRead, markAllNotificationsRead, resetDemo } = useHGS();
  const navigate = useNavigate();
  const role = ROLES.find((r) => r.id === user?.role);
  const [open, setOpen] = useState(false);
  const dropRef = useRef(null);
  const toastedIdsRef = useRef(new Set());

  const myNotifications = useMemo(() => (notifications || []).filter((n) => {
    if (!user) return false;
    if (n.targetRole && n.targetRole !== user.role) return false;
    if (n.targetDepartment && n.targetDepartment !== user.department) return false;
    return true;
  }), [notifications, user]);

  const unread = useMemo(() => myNotifications.filter((n) => !n.readBy.includes(user?.userId)), [myNotifications, user]);

  // Fire toast once per new unread notification for current user
  useEffect(() => {
    if (!user) return;
    unread.forEach((n) => {
      if (toastedIdsRef.current.has(n.id)) return;
      toastedIdsRef.current.add(n.id);
      const meta = TYPE_META[n.type] || TYPE_META.handoff;
      const action = n.link
        ? { label: 'View', onClick: () => { markNotificationRead(n.id, user.userId); navigate(n.link); } }
        : undefined;
      if (n.type === 'handoff') toast.info(n.title, { description: n.body, action });
      else if (n.type === 'approved') toast.success(n.title, { description: n.body, action });
      else if (n.type === 'rejected') toast.error(n.title, { description: n.body, action });
      else toast(n.title, { description: n.body, action });
    });
  }, [unread, user, markNotificationRead, navigate]);

  // Click outside → close
  useEffect(() => {
    const onClick = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handleOpen = (n) => {
    if (!user) return;
    markNotificationRead(n.id, user.userId);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const handleMarkAll = () => { if (user) markAllNotificationsRead(user.userId); };

  return (
    <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
      <div className="px-8 py-4 flex items-center justify-between gap-6">
        <div className="min-w-0 flex-1">
          {breadcrumbs.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
              {breadcrumbs.map((bc, i) => (
                <React.Fragment key={`${i}-${bc}`}>
                  <span>{bc}</span>
                  {i < breadcrumbs.length - 1 && <ChevronRight className="w-3 h-3" />}
                </React.Fragment>
              ))}
            </div>
          )}
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight truncate">{title}</h1>
          {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
        </div>

        <div className="flex items-center gap-3">
          {actions}
          <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200 w-64">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              placeholder="Search risks, items..."
              className="bg-transparent outline-none text-sm flex-1 placeholder:text-slate-400"
            />
          </div>

          {/* Demo Reset — wipes all localStorage state and returns to /login */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                data-testid="demo-reset-btn"
                title="Reset demo state for a fresh walkthrough"
                className="hidden sm:inline-flex items-center gap-1.5 px-2.5 h-10 rounded-lg bg-slate-50 border border-slate-200 hover:bg-slate-100 text-[11px] font-semibold text-slate-700 transition"
              >
                <RefreshCw className="w-3.5 h-3.5 text-slate-500" />
                <span className="hidden lg:inline">Reset Demo</span>
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="demo-reset-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Reset demo state?</AlertDialogTitle>
                <AlertDialogDescription>
                  This wipes all local responses, master uploads, version history, countermeasures and notifications,
                  and re-seeds the demo with the original Risk Universe (v3.2). You&apos;ll be returned to the login screen.
                  <span className="block mt-2 text-[11px] text-amber-700 font-medium">
                    Use this for a clean executive walkthrough. The action cannot be undone.
                  </span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="demo-reset-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="demo-reset-confirm"
                  onClick={resetDemo}
                  className="bg-[#CC0000] hover:bg-[#B00000] text-white"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  Reset &amp; restart
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Notifications */}
          <div className="relative" ref={dropRef}>
            <button
              data-testid="notifications-bell"
              onClick={() => setOpen((v) => !v)}
              className="relative w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 hover:bg-slate-100 flex items-center justify-center transition"
            >
              <Bell className="w-4 h-4 text-slate-600" />
              {unread.length > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#CC0000] text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white"
                  data-testid="notifications-badge"
                >
                  {unread.length}
                </span>
              )}
            </button>

            {open && (
              <div
                data-testid="notifications-dropdown"
                className="absolute right-0 mt-2 w-[380px] bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden z-50"
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                  <div>
                    <div className="text-sm font-bold text-slate-900">Notifications</div>
                    <div className="text-[10px] text-slate-500">{unread.length} unread · {myNotifications.length} total</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {unread.length > 0 && (
                      <button
                        data-testid="mark-all-read"
                        onClick={handleMarkAll}
                        className="text-[11px] font-bold text-[#CC0000] hover:underline"
                      >
                        Mark all read
                      </button>
                    )}
                    <button onClick={() => setOpen(false)} className="w-6 h-6 rounded hover:bg-slate-100 flex items-center justify-center text-slate-500">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="max-h-[420px] overflow-y-auto">
                  {myNotifications.length === 0 ? (
                    <div className="p-8 text-center text-xs text-slate-500" data-testid="notifications-empty">
                      <Bell className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                      No notifications yet.
                    </div>
                  ) : (
                    myNotifications.map((n) => {
                      const meta = TYPE_META[n.type] || TYPE_META.handoff;
                      const Icon = meta.icon;
                      const isUnread = !n.readBy.includes(user?.userId);
                      return (
                        <button
                          key={n.id}
                          data-testid={`notif-${n.id}`}
                          onClick={() => handleOpen(n)}
                          className={cn(
                            'w-full text-left p-3 border-b border-slate-100 hover:bg-slate-50 flex items-start gap-3 transition',
                            isUnread && 'bg-amber-50/40'
                          )}
                        >
                          <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center border flex-shrink-0', meta.color)}>
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className={cn('text-xs font-bold', isUnread ? 'text-slate-900' : 'text-slate-600')}>{n.title}</span>
                              {isUnread && <span className="w-1.5 h-1.5 rounded-full bg-[#CC0000]" />}
                            </div>
                            <p className="text-[11px] text-slate-600 leading-snug">{n.body}</p>
                            <div className="text-[9px] text-slate-400 mt-1">{new Date(n.createdAt).toLocaleString()}</div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-slate-50 to-white border border-slate-200">
            <span className="w-1.5 h-1.5 rounded-full bg-[#CC0000]" />
            <span className="text-xs font-semibold text-slate-700">{role?.label}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
